import { Component } from 'react';
import { AmbitClient } from '@ambit-ai/ambit-client';
import { connect } from 'react-redux';
import withConfig from '../../context/withConfig';
import {
  USER_LOCAL_STORAGE_KEY,
  CONVERSATION_ID_LOCAL_STORAGE_KEY
} from '../../initialiser/constants';

import { startConnection, connectionChange, onlineChange } from '../../reducers/connection/actions';

import {
  sendTextMessage,
  receiveMessage,
  receiveSentMessage,
  showTyping,
  clearTyping,
  sendUserData,
  receiveOperatorEvent,
  clearHistory
} from '../../reducers/history/actions';

let connectionStarted = false;

class WebSocketConnection extends Component {
  constructor(props) {
    super(props);

    this.state = {
      timeout: null,
      isNewUser: false,
      toReinitiate: false,
      pastUser: ''
    };
  }

  storeUserToLocalStorage(userDetails, conversationId) {
    window.localStorage.setItem(USER_LOCAL_STORAGE_KEY, userDetails);
    window.localStorage.setItem(CONVERSATION_ID_LOCAL_STORAGE_KEY, conversationId);
  }

  componentDidUpdate() {
    const {
      config,
      startConnection,
      connectionChange,
      sendTextMessage,
      sendUserData,
      chatPanelVisible,
      clearHistory
    } = this.props;

    // For createNewUser API
    if (this.state.isNewUser) {
      // We want to tell the WebSocketConnection that the connection has not started yet
      connectionStarted = false;
    }

    if (!chatPanelVisible || connectionStarted) {
      return;
    }

    if (!config.useSocketIo) {
      config.usePolling = true;
    }

    if (config.usePolling) {
      config.directLine.webSocket = false;
    }

    if (this.state.isNewUser && this.state.toReinitiate) {
      // Based on the observation of when we clear the localStorage and refresh the page,
      // These fields are the only one missing in the JSON config
      // We want to tell the socket that the connection is new again
      delete config.directLine.user;
      config.directLine.conversationId = null;
      config.user = null;

      this.setState({ isNewUser: false });
    }

    config.directLine.user = config.user;

    const botConnection = new AmbitClient({
      baseUrl: config.botBaseUrl,
      secret: config.directLine.secret,
      conversationId: config.directLine.conversationId,
      usePolling: config.usePolling,
      user: config.directLine.user
    });

    startConnection(botConnection);

    this.connectionStatusSubscription = botConnection.connectionStatus$.subscribe(
      connectionStatus => {
        // Storing current user details as JSON string in localStorage
        const userDetails = JSON.stringify(botConnection.user);

        if (userDetails && connectionStatus === 2) {
          // When we trigger createNewUser API, it does not kill the previous sockets
          // The previous sockets are still going in and out to the connection subscription
          // after we forcefully create the new user

          // Check if current user is not the past user
          // We have to make sure that we are storing the user we want to the localStorage
          if (this.state.toReinitiate && userDetails !== this.state.pastUser) {
            this.storeUserToLocalStorage(userDetails, botConnection.conversationId);
            this.setState({ pastUser: '' });
          } else if (!this.state.toReinitiate) {
            this.storeUserToLocalStorage(userDetails, botConnection.conversationId);
          }
        }

        connectionChange({ connectionStatus, botConnection });

        // Proactive message to be sent after the connection to a new user is made
        // This is only for createNewUser API
        // Have a look at the visibilityChangeHandler for the REAL
        // init of proactive message
        if (this.state.toReinitiate && connectionStatus === 2) {
          this.setState({ toReinitiate: false });

          if (config.proactiveMessage) {
            window.parent.postMessage(
              {
                source: 'ambitChatPanel',
                type: 'sendProactiveMessage',
                text: config.proactiveMessage
              },
              '*'
            );
          }
        }
      }
    );

    this.activitySubscription = botConnection.activity$.subscribe(
      this.handleIncomingActivity,
      error => console.log('activity$ error', error)
    );

    // To make sure that we only add an event listener once to the browser
    // addEventListener apparently stack itself to the browser
    if (!this.state.toReinitiate) {
      // Listen for events from the parent page
      window.addEventListener('message', e => {
        if (e.data.type === 'sendMessage') {
          sendTextMessage({ text: e.data.text });
        } else if (e.data.type === 'sendUserData') {
          sendUserData({ data: e.data.data });
        } else if (e.data.type === 'createNewUser') {
          const { connectionStatusSubscription, activitySubscription } = this;

          // Cancel all subscriptions
          if (connectionStatusSubscription) {
            connectionStatusSubscription.unsubscribe();
          }
          if (activitySubscription) {
            activitySubscription.unsubscribe();
          }

          // Clear localStorage and history
          const recentUser = window.localStorage.getItem(USER_LOCAL_STORAGE_KEY);
          window.localStorage.clear();
          clearHistory();

          // Notify parent window to clear UI
          window.parent.postMessage(
            {
              source: 'ambitChatPanel',
              type: 'clearChatPanel'
            },
            '*'
          );

          // Set state to trigger creation of new connection
          this.setState({
            isNewUser: true,
            toReinitiate: true,
            pastUser: recentUser
          });
        }
      });
    }

    connectionStarted = true;
  }

  componentWillUnmount() {
    this.connectionStatusSubscription.unsubscribe();
    this.activitySubscription.unsubscribe();
  }

  handleIncomingActivity = activity => {
    const {
      connection,
      receiveMessage,
      receiveSentMessage,
      showTyping,
      clearTyping,
      receiveOperatorEvent
    } = this.props;

    window.parent.postMessage(
      {
        source: 'ambitChatLauncher',
        type: 'activity',
        data: activity
      },
      '*'
    );

    if (
      activity.type === 'operatorHandOver' ||
      activity.type === 'operatorHandBack' ||
      activity.type === 'operatorRequest' ||
      activity.name === 'operatorRequest' ||
      activity.type === 'operatorRequestTimeout' ||
      activity.name === 'operatorRequestTimeout'
    ) {
      receiveOperatorEvent(activity);
    }
    const clearTypingIndicator = () => {
      const { timeout } = this.state;
      clearTimeout(timeout);
      this.setState({
        timeout: setTimeout(() => {
          clearTyping({ activity });
        }, 60000)
      });
    };
    if (activity.type === 'message') {
      if (
        activity.from &&
        connection &&
        connection.user &&
        activity.from.id === connection.user.id
      ) {
        receiveSentMessage({ activity });
      } else {
        receiveMessage({ activity });
      }
    } else if (activity.type === 'typing') {
      if (
        activity.from &&
        connection &&
        connection.user &&
        activity.from.id !== connection.user.id
      ) {
        showTyping({ activity });
        clearTypingIndicator();
      }
    }
  };

  render() {
    return null;
  }
}

export default connect(
  state => ({
    connection: state.connection,
    chatPanelVisible: state.ui.chatPanelVisible,
    showIntro: state.ui.showIntro
  }),
  {
    startConnection,
    connectionChange,
    sendTextMessage,
    receiveMessage,
    receiveSentMessage,
    showTyping,
    clearTyping,
    sendUserData,
    onlineChange,
    receiveOperatorEvent,
    clearHistory
  }
)(withConfig(WebSocketConnection));



// WEBPACK FOOTER //
// ./src/components/WebSocketConnection/index.js